import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import SectionBg from '../components/SectionBg';
import { categories as cats, occasions as occs, contest } from '../data/recipes';

const emptyIngredient = { name: '', quantity: '', unit: '', cost: '' };

const AddRecipePage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [enterContest, setEnterContest] = useState(params.get('concours') === '1');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    name: '', description: '', category: '', occasions: [],
    prep_time: '', estimated_cost: '', image_url: '', tartina_quantity: '',
    ingredients: [{ ...emptyIngredient }],
    steps: [''],
    tips: [''],
  });

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const toggleOccasion = (o) =>
    setForm((p) => ({
      ...p,
      occasions: p.occasions.includes(o)
        ? p.occasions.filter((x) => x !== o)
        : [...p.occasions, o],
    }));

  const setList = (key, i, value) =>
    setForm((p) => {
      const arr = [...p[key]];
      arr[i] = value;
      return { ...p, [key]: arr };
    });

  const addItem = (key, blank) => setForm((p) => ({ ...p, [key]: [...p[key], blank] }));
  const removeItem = (key, i) =>
    setForm((p) => (p[key].length <= 1 ? p : { ...p, [key]: p[key].filter((_, j) => j !== i) }));

  const setIngredient = (i, field, value) =>
    setForm((p) => {
      const arr = [...p.ingredients];
      arr[i] = { ...arr[i], [field]: value };
      return { ...p, ingredients: arr };
    });

  const ingredientsTotal = useMemo(
    () => form.ingredients.reduce((s, ing) => s + (Number(ing.cost) || 0), 0),
    [form.ingredients]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.description || !form.category) {
      setError('Renseigne au moins le nom, la description et la catégorie.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setError(null);
    setLoading(true);
    // Maquette : soumission simulée (pas de backend pour le moment)
    await new Promise((r) => setTimeout(r, 700));
    // eslint-disable-next-line no-console
    console.log('Recette ajoutée (maquette) :', { ...form, estimated_cost: ingredientsTotal, enterContest });
    navigate(enterContest ? '/concours' : '/recipes');
  };

  return (
    <>
      <Banner>
        <SectionBg tone="chocolate" variant={2} />
        <Inner>
          <Eyebrow><i className="ti ti-plus" aria-hidden="true" /> Contribuer au catalogue</Eyebrow>
          <h1>Ajouter une recette Tartina</h1>
          <p>Partage ta création avec la communauté. Tu pourras, si tu le souhaites, l'inscrire aussi au jeu-concours du mois.</p>
        </Inner>
      </Banner>

      <Inner>
        {error && <ErrorBox><i className="ti ti-alert-triangle" aria-hidden="true" /> {error}</ErrorBox>}

        <Form onSubmit={handleSubmit}>
          {/* Infos générales */}
          <Card>
            <CardTitle><i className="ti ti-info-circle" aria-hidden="true" /> Informations générales</CardTitle>
            <Field>
              <label>Nom de la recette *</label>
              <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Ex : Brownie fondant Tartina" />
            </Field>
            <Field>
              <label>Description *</label>
              <textarea rows="3" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Décris ta recette en quelques mots gourmands…" />
            </Field>
            <Field>
              <label>Catégorie *</label>
              <select value={form.category} onChange={(e) => set('category', e.target.value)}>
                <option value="">Choisir…</option>
                {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>
            <Field>
              <label>Occasions <Hint>(plusieurs possibles)</Hint></label>
              <OccChips>
                {occs.map((o) => {
                  const active = form.occasions.includes(o);
                  return (
                    <OccChip key={o} type="button" $active={active} onClick={() => toggleOccasion(o)}>
                      {active && <i className="ti ti-check" aria-hidden="true" />}
                      {o.charAt(0).toUpperCase() + o.slice(1)}
                    </OccChip>
                  );
                })}
              </OccChips>
            </Field>
            <Row>
              <Field>
                <label>Temps de préparation (min)</label>
                <input type="number" min="1" value={form.prep_time} onChange={(e) => set('prep_time', e.target.value)} />
              </Field>
              <Field>
                <label>Cuillères / quantité de Tartina</label>
                <input value={form.tartina_quantity} onChange={(e) => set('tartina_quantity', e.target.value)} placeholder="Ex : 4 cuillères" />
              </Field>
            </Row>
            <Field>
              <label>URL de l'image</label>
              <input type="url" value={form.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="https://…" />
            </Field>
          </Card>

          {/* Ingrédients */}
          <Card>
            <CardTitle><i className="ti ti-basket" aria-hidden="true" /> Ingrédients</CardTitle>
            {form.ingredients.map((ing, i) => (
              <IngRow key={i}>
                <input placeholder="Ingrédient" value={ing.name} onChange={(e) => setIngredient(i, 'name', e.target.value)} />
                <input placeholder="Qté" value={ing.quantity} onChange={(e) => setIngredient(i, 'quantity', e.target.value)} style={{ maxWidth: 80 }} />
                <input placeholder="Unité" value={ing.unit} onChange={(e) => setIngredient(i, 'unit', e.target.value)} style={{ maxWidth: 90 }} />
                <input placeholder="Coût (F)" type="number" min="0" value={ing.cost} onChange={(e) => setIngredient(i, 'cost', e.target.value)} style={{ maxWidth: 110 }} />
                <RemoveBtn type="button" onClick={() => removeItem('ingredients', i)} aria-label="Supprimer"><i className="ti ti-x" /></RemoveBtn>
              </IngRow>
            ))}
            <AddRow type="button" onClick={() => addItem('ingredients', { ...emptyIngredient })}>
              <i className="ti ti-plus" aria-hidden="true" /> Ajouter un ingrédient
            </AddRow>
            <TotalRow><span>Coût total estimé</span><b>{ingredientsTotal.toLocaleString('fr-FR')} FCFA</b></TotalRow>
          </Card>

          {/* Étapes */}
          <Card>
            <CardTitle><i className="ti ti-list-numbers" aria-hidden="true" /> Étapes de préparation</CardTitle>
            {form.steps.map((step, i) => (
              <StepRow key={i}>
                <StepNum>{i + 1}</StepNum>
                <textarea rows="2" value={step} onChange={(e) => setList('steps', i, e.target.value)} placeholder={`Étape ${i + 1}`} />
                <RemoveBtn type="button" onClick={() => removeItem('steps', i)} aria-label="Supprimer"><i className="ti ti-x" /></RemoveBtn>
              </StepRow>
            ))}
            <AddRow type="button" onClick={() => addItem('steps', '')}>
              <i className="ti ti-plus" aria-hidden="true" /> Ajouter une étape
            </AddRow>
          </Card>

          {/* Astuces */}
          <Card>
            <CardTitle><i className="ti ti-bulb" aria-hidden="true" /> Astuces Tartina</CardTitle>
            {form.tips.map((tip, i) => (
              <IngRow key={i}>
                <input value={tip} onChange={(e) => setList('tips', i, e.target.value)} placeholder="Un conseil pour réussir / sublimer avec Tartina" />
                <RemoveBtn type="button" onClick={() => removeItem('tips', i)} aria-label="Supprimer"><i className="ti ti-x" /></RemoveBtn>
              </IngRow>
            ))}
            <AddRow type="button" onClick={() => addItem('tips', '')}>
              <i className="ti ti-plus" aria-hidden="true" /> Ajouter une astuce
            </AddRow>
          </Card>

          {/* Participation au jeu-concours (optionnelle) */}
          <OptIn $on={enterContest}>
            <OptInToggle>
              <input
                type="checkbox"
                checked={enterContest}
                onChange={(e) => setEnterContest(e.target.checked)}
              />
              <span className="box"><i className="ti ti-check" aria-hidden="true" /></span>
              <div>
                <b><i className="ti ti-trophy" aria-hidden="true" /> Participer au jeu-concours « {contest.month} »</b>
                <small>
                  Optionnel. Ta recette entre au classement et peut gagner {contest.prize}. Clôture le {contest.deadline}.
                </small>
              </div>
            </OptInToggle>
          </OptIn>

          <Submit type="submit" disabled={loading}>
            {loading ? 'Envoi…' : (
              enterContest ? (
                <><i className="ti ti-trophy" aria-hidden="true" /> Ajouter et participer au concours</>
              ) : (
                <><i className="ti ti-plus" aria-hidden="true" /> Ajouter ma recette</>
              )
            )}
          </Submit>
        </Form>
      </Inner>
    </>
  );
};

const Banner = styled.section`
  background: ${({ theme }) => theme.colors.secondary}; padding: 40px 0 34px;
  position: relative; isolation: isolate;
  h1 { font-size: clamp(1.9rem, 4vw, 2.7rem); color: ${({ theme }) => theme.colors.chocolate}; margin: 10px 0 8px; }
  p { color: ${({ theme }) => theme.colors.chocolate}; opacity: 0.85; margin: 0; max-width: 56ch; }
`;
const Inner = styled.div`max-width: 820px; margin: 0 auto; padding: 0 24px;`;
const Eyebrow = styled.span`
  display: inline-flex; align-items: center; gap: 6px; background: ${({ theme }) => theme.colors.primary};
  color: #fff; font-weight: 800; font-size: 0.8rem; padding: 6px 14px; border-radius: 999px;
`;
const ErrorBox = styled.div`
  display: flex; align-items: center; gap: 8px; margin-top: 24px;
  background: #FDE7E7; color: ${({ theme }) => theme.colors.primaryDark};
  border-radius: 14px; padding: 14px 18px; font-weight: 700;
`;
const Form = styled.form`padding: 28px 0 8px; display: flex; flex-direction: column; gap: 22px;`;
const Card = styled.div`
  background: #fff; border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 20px; padding: 24px;
`;
const CardTitle = styled.h2`
  display: flex; align-items: center; gap: 8px; font-size: 1.3rem; color: ${({ theme }) => theme.colors.chocolate};
  margin-bottom: 18px; i { color: ${({ theme }) => theme.colors.primary}; }
`;
const Field = styled.div`
  display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; flex: 1;
  label { font-weight: 800; font-size: 0.85rem; color: ${({ theme }) => theme.colors.chocolate}; }
  input, textarea, select {
    font-family: inherit; font-size: 0.95rem; padding: 11px 14px; border-radius: 12px;
    border: 1px solid ${({ theme }) => theme.colors.border}; background: ${({ theme }) => theme.colors.background};
    outline: none; transition: border-color .15s ease, box-shadow .15s ease; color: ${({ theme }) => theme.colors.textPrimary};
  }
  input:focus, textarea:focus, select:focus {
    border-color: ${({ theme }) => theme.colors.secondaryDark};
    box-shadow: 0 0 0 3px rgba(255,221,0,0.25); background: #fff;
  }
  textarea { resize: vertical; }
`;
const Row = styled.div`
  display: flex; gap: 16px;
  @media (max-width: 560px) { flex-direction: column; gap: 0; }
`;
const Hint = styled.span`
  font-weight: 600; font-size: 0.78rem; color: ${({ theme }) => theme.colors.textSecondary};
`;
const OccChips = styled.div`display: flex; flex-wrap: wrap; gap: 10px;`;
const OccChip = styled.button`
  display: inline-flex; align-items: center; gap: 5px; cursor: pointer;
  border: 2px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : '#fff')};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.chocolate)};
  font-weight: 800; font-size: 0.88rem; padding: 8px 15px; border-radius: 999px;
  transition: all .15s ease; font-family: inherit;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
  i { font-size: 0.95rem; }
`;
const IngRow = styled.div`
  display: flex; gap: 10px; margin-bottom: 10px; align-items: center;
  input {
    flex: 1; font-family: inherit; font-size: 0.92rem; padding: 10px 12px; border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.colors.border}; background: ${({ theme }) => theme.colors.background}; outline: none;
  }
  input:focus { border-color: ${({ theme }) => theme.colors.secondaryDark}; background: #fff; }
`;
const StepRow = styled.div`
  display: flex; gap: 10px; margin-bottom: 10px; align-items: flex-start;
  textarea {
    flex: 1; font-family: inherit; font-size: 0.92rem; padding: 10px 12px; border-radius: 10px;
    border: 1px solid ${({ theme }) => theme.colors.border}; background: ${({ theme }) => theme.colors.background}; outline: none; resize: vertical;
  }
  textarea:focus { border-color: ${({ theme }) => theme.colors.secondaryDark}; background: #fff; }
`;
const StepNum = styled.span`
  flex: none; width: 32px; height: 32px; border-radius: 50%; margin-top: 4px;
  background: ${({ theme }) => theme.colors.primary}; color: #fff; font-weight: 800;
  display: flex; align-items: center; justify-content: center; font-family: ${({ theme }) => theme.fonts.heading};
`;
const RemoveBtn = styled.button`
  flex: none; width: 34px; height: 34px; border-radius: 10px; cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border}; background: #fff; color: ${({ theme }) => theme.colors.primary};
  display: inline-flex; align-items: center; justify-content: center; font-size: 1.1rem;
  &:hover { background: #FDE7E7; }
`;
const AddRow = styled.button`
  width: 100%; margin-top: 6px; cursor: pointer;
  border: 1.5px dashed ${({ theme }) => theme.colors.secondaryDark}; background: transparent;
  color: ${({ theme }) => theme.colors.tertiary}; font-weight: 800; font-size: 0.92rem;
  padding: 11px; border-radius: 12px; display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  &:hover { background: ${({ theme }) => theme.colors.cream}; }
`;
const TotalRow = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-top: 14px;
  background: ${({ theme }) => theme.colors.background}; border-radius: 12px; padding: 12px 16px;
  span { font-weight: 700; color: ${({ theme }) => theme.colors.textSecondary}; }
  b { font-family: ${({ theme }) => theme.fonts.heading}; font-size: 1.2rem; color: ${({ theme }) => theme.colors.primary}; }
`;
const Submit = styled.button`
  align-self: center; display: inline-flex; align-items: center; gap: 8px; cursor: pointer;
  border: none; border-radius: 999px; padding: 15px 36px; font-weight: 800; font-size: 1.05rem;
  background: ${({ theme }) => theme.colors.primary}; color: #fff; transition: all .2s ease;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.primaryDark}; transform: translateY(-2px); }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const OptIn = styled.div`
  background: ${({ $on, theme }) => ($on ? theme.colors.cream : '#fff')};
  border: 2px solid ${({ $on, theme }) => ($on ? theme.colors.secondaryDark : theme.colors.border)};
  border-radius: 20px; padding: 18px 22px; transition: all .15s ease;
`;
const OptInToggle = styled.label`
  display: flex; align-items: flex-start; gap: 14px; cursor: pointer;
  input { position: absolute; opacity: 0; width: 0; height: 0; }
  .box {
    flex: none; width: 26px; height: 26px; border-radius: 8px; margin-top: 2px;
    border: 2px solid ${({ theme }) => theme.colors.secondaryDark}; background: #fff;
    display: inline-flex; align-items: center; justify-content: center;
    color: #fff; font-size: 1rem; transition: all .15s ease;
  }
  input:checked + .box { background: ${({ theme }) => theme.colors.primary}; border-color: ${({ theme }) => theme.colors.primary}; }
  input:not(:checked) + .box i { opacity: 0; }
  b { display: flex; align-items: center; gap: 6px; color: ${({ theme }) => theme.colors.chocolate}; font-size: 1.02rem; }
  b i { color: ${({ theme }) => theme.colors.primary}; }
  small { display: block; color: ${({ theme }) => theme.colors.textSecondary}; margin-top: 4px; font-size: 0.9rem; line-height: 1.5; }
`;

export default AddRecipePage;
